import { gql, useApolloClient } from "@apollo/client";
import {
  CardContainer,
  getFormattedPercentage,
  getFormattedPrice,
  LabeledValue,
} from "@msys/ui";
import { InfoOutlined as InfoOutlinedIcon } from "@mui/icons-material";
import { Divider, Grid, Stack, Tooltip, Typography } from "@mui/material";
import { useTolgee } from "@tolgee/react";
import { Form, Formik, FormikProps } from "formik";
import { AutoSave } from "../../../commons/form-fields/AutoSave.js";
import { FormattedFloatField } from "../../../commons/form-fields/FormattedFloatField.js";
import { px } from "../../../../common/MuiThemeProvider.js";
import {
  MaterialCalculationActualRow_ItemFragment,
  MaterialCalculationBox_ItemFragment,
  MaterialCalculationBox_ProjectFragment,
  MaterialCalculationEstimatedRow_ItemFragment,
  useQuoteModifyItem_MaterialCalculationBoxMutation,
} from "./MaterialCalculationBox.generated.js";
import { useTranslate } from "@tolgee/react";
import { LabeledQuantityValue } from "../LabeledQuantityValue.js";
import { useQuantityUnits } from "../useQuantityUnits.js";

interface FormValues {
  actualQuantity: number;
}

interface Props {
  project: MaterialCalculationBox_ProjectFragment;
  docId: string;
  item: MaterialCalculationBox_ItemFragment;
  refetchQueriesOnAction?: string[];
  isInitiallyClosed?: boolean;
}

export const MaterialCalculationBox = ({
  project,
  docId,
  item,
  isInitiallyClosed,
  refetchQueriesOnAction,
}: Props) => {
  const { t } = useTranslate("QuoteItem");

  return (
    <CardContainer
      isExpandable={true}
      title={t("Material Calculation")}
      isInitiallyClosed={isInitiallyClosed}
    >
      <Stack
        direction="column"
        spacing={1}
        sx={{
          p: 1,
        }}
      >
        {item.type === "paid" && (
          <>
            <MaterialCalculationEstimatedRow item={item} />

            <Divider />

            <MaterialCalculationActualRow
              projectId={project.id}
              docId={docId}
              item={item}
              refetchQueriesOnAction={refetchQueriesOnAction}
              canEdit={item.canBeWorkedOn}
            />
          </>
        )}
      </Stack>
    </CardContainer>
  );
};

const MaterialCalculationEstimatedRow = ({
  item,
}: {
  item: MaterialCalculationEstimatedRow_ItemFragment;
}) => {
  const { t } = useTranslate("QuoteItem");
  const language = useTolgee(["language"]).getLanguage()!;

  const calculation =
    item?.invoiceAgreedOrProposed === "agreed"
      ? item?.agreedCalculation
      : item?.proposedCalculation;

  const materialPricePerUnit = calculation?.materialPricePerUnit ?? 0;
  const materialBuyingPricePerUnit =
    calculation?.materialBuyingPricePerUnit ?? 0;
  const materialMargin = calculation?.materialMargin ?? 0;
  const materialPriceSubTotal = calculation?.materialPriceSubTotal ?? 0;

  return (
    <Stack direction="column" spacing={1 / 2}>
      <Typography color="primary" variant="h3">
        {t("Estimated")}
      </Typography>
      <Grid container direction="row">
        <Grid
          style={{
            flexBasis: `calc(33.333333% - ${px.l})`,
            maxWidth: `calc(33.333333% - ${px.l})`,
          }}
          size={4}
        >
          <LabeledQuantityValue
            value={item.estimatedQuantity}
            unit={item.quantityUnit}
            label={t("Quantity")}
            color="primary"
          />
        </Grid>
        <Grid
          container
          style={{ width: px.l }}
          sx={{
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          x
        </Grid>
        <Grid
          container
          style={{
            flexBasis: `calc(33.333333% - ${px.l})`,
            maxWidth: `calc(33.333333% - ${px.l})`,
          }}
          size={4}
          sx={{
            justifyContent: "flex-start",
            alignItems: "center",
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: "center",
            }}
          >
            <LabeledValue label={t("Price / Unit")}>
              {getFormattedPrice(materialPricePerUnit, language)}
            </LabeledValue>
            {materialMargin > 0 && (
              <Tooltip
                title={`${t("Net price")}: ${getFormattedPrice(
                  materialBuyingPricePerUnit,
                  language
                )} + ${getFormattedPercentage(materialMargin, language)}`}
              >
                <InfoOutlinedIcon fontSize="small" color="secondary" />
              </Tooltip>
            )}
          </Stack>
        </Grid>
        <Grid
          container
          style={{ width: px.l }}
          sx={{
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          =
        </Grid>
        <Grid
          container
          size={4}
          sx={{
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          <LabeledValue label={t("Total price")}>
            {getFormattedPrice(materialPriceSubTotal, language)}
          </LabeledValue>
        </Grid>
      </Grid>
    </Stack>
  );
};

const MaterialCalculationActualRow = ({
  docId,
  projectId,
  item,
  refetchQueriesOnAction,
  canEdit,
}: {
  projectId: string;
  docId: string;
  item: MaterialCalculationActualRow_ItemFragment;
  refetchQueriesOnAction?: string[];
  canEdit: boolean;
}) => {
  const { t } = useTranslate("QuoteItem");
  const language = useTolgee(["language"]).getLanguage()!;

  const client = useApolloClient();
  const [modifyItemActualCalculation] =
    useQuoteModifyItem_MaterialCalculationBoxMutation({
      client,
    });

  const { quantityUnitLabels } = useQuantityUnits();

  const initialValues: FormValues = {
    actualQuantity: item.actualQuantity || item.estimatedQuantity,
  };

  const onSubmit = async (values: FormValues) => {
    await modifyItemActualCalculation({
      variables: {
        input: {
          projectId,
          docId,
          itemId: item.id,
          values: {
            actualQuantity: values.actualQuantity,
          },
        },
      },
      refetchQueries: refetchQueriesOnAction,
    });
  };

  return (
    <Stack direction="column" spacing={1 / 2}>
      <Typography color="primary" variant="h3">
        {t("Actual")}
      </Typography>
      <Formik<FormValues> initialValues={initialValues} onSubmit={onSubmit}>
        {({ values }: FormikProps<FormValues>) => (
          <Form>
            <Grid container direction="row">
              <Grid
                style={{
                  flexBasis: `calc(33.333333% - ${px.l})`,
                  maxWidth: `calc(33.333333% - ${px.l})`,
                }}
                size={4}
              >
                {canEdit ? (
                  <FormattedFloatField
                    disabled={false}
                    label={t("Quantity")}
                    name="actualQuantity"
                    type="float"
                    min={0}
                    unit={"\u00A0" + quantityUnitLabels[item.quantityUnit]}
                  />
                ) : (
                  <LabeledQuantityValue
                    value={item.actualQuantity || item.estimatedQuantity}
                    unit={item.quantityUnit}
                    label={t("Quantity")}
                    color="primary"
                  />
                )}
              </Grid>
              <Grid
                container
                style={{
                  width: px.l,
                  paddingBottom: px.xs,
                }}
                sx={{
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                x
              </Grid>
              <Grid
                container
                style={{
                  flexBasis: `calc(33.333333% - ${px.l})`,
                  maxWidth: `calc(33.333333% - ${px.l})`,
                }}
                size={4}
                sx={{
                  alignItems: "center",
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    alignItems: "center",
                  }}
                >
                  <LabeledValue label={t("Price / Unit")}>
                    {getFormattedPrice(
                      item.averageMaterialBuyingPrice,
                      language
                    )}
                  </LabeledValue>
                  <Tooltip
                    title={t("An average buying price from Purchase Orders")}
                  >
                    <InfoOutlinedIcon fontSize="small" color="secondary" />
                  </Tooltip>
                </Stack>
              </Grid>
              <Grid
                container
                style={{
                  width: px.l,
                  paddingBottom: px.xs,
                }}
                sx={{
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                =
              </Grid>
              <Grid
                container
                size={4}
                sx={{
                  justifyContent: "flex-end",
                  alignItems: "center",
                }}
              >
                <Stack
                  direction="row"
                  spacing={1 / 2}
                  sx={{
                    alignItems: "center",
                  }}
                >
                  <LabeledValue label={t("Total price")}>
                    {getFormattedPrice(
                      values.actualQuantity * item.averageMaterialBuyingPrice,
                      language
                    )}
                  </LabeledValue>
                </Stack>
              </Grid>
            </Grid>
            <AutoSave enableReinitialize initialValues={initialValues} />
          </Form>
        )}
      </Formik>
    </Stack>
  );
};
